// Die Leitung als Vorschlag in der Karte "Neu" unter "Arbeitet für"
// (ST-PTY-001, Signatur S3, Vorgabe DESIGN V5 und V6).
//
// Eine benannte Gruppe (fieldset/legend) mit je einem Haekchen pro Person,
// standardmaessig aus. Kein "Alle wählen": drei Haekchen sind schneller gesetzt
// als erklaert, und "alle" waere die automatische Loesung durch die Hintertuer.
// Ohne erkannte Leitung steht nichts da, auch kein Leertext.

import type { ReactElement } from 'react';
import { Body1, Caption1, Checkbox, makeStyles, tokens } from '@fluentui/react-components';

import {
  beziehungenAnsehen,
  LEITUNG_ABGLEICH_GESCHEITERT,
  LEITUNG_ABGLEICH_GRENZE,
  LEITUNG_ABGLEICH_TITEL,
  leitungAbgleichAnsage,
  LEITUNG_DATENSCHUTZ,
  leitungNichtMehr,
  leitungSatz,
  leitungSchonDa,
  leitungTitel,
  type LeitungAbgleich,
  type LeitungName,
} from '../leitung.ts';
import { partyHref } from '../parties.ts';
import { NeuerTabZeichen } from '../shell/symbole.tsx';
import { textVorNeuerTab } from '../neuer-tab.ts';

const useStyles = makeStyles({
  // Optisch verborgen, fuer Hilfsmittel da (wie die Ladeansage im Skelett).
  verborgen: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
  gruppe: {
    border: 'none',
    margin: 0,
    padding: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    // Der Fokus nach einer Ablehnung landet auf der Gruppe; sie zeigt ihn wie ein Feld.
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '2px' },
  },
  legende: {
    padding: 0,
    fontWeight: tokens.fontWeightSemibold,
    overflowWrap: 'break-word',
  },
  gedaempft: { color: tokens.colorNeutralForeground2 },
});

export function LeitungGruppe({
  organisation,
  namen,
  gewaehlt,
  onChange,
  disabled,
}: {
  readonly organisation: string;
  readonly namen: readonly LeitungName[];
  readonly gewaehlt: ReadonlySet<string>;
  readonly onChange: (anzeige: string, an: boolean) => void;
  readonly disabled: boolean;
}): ReactElement {
  const styles = useStyles();
  return (
    <fieldset id="qc-leitung" tabIndex={-1} className={styles.gruppe}>
      <legend className={styles.legende}>{leitungTitel(namen.length, organisation)}</legend>
      <Caption1>{leitungSatz(organisation)}</Caption1>
      {namen.map((name) => (
        <Checkbox
          key={name.anzeige}
          label={name.anzeige}
          checked={gewaehlt.has(name.anzeige)}
          disabled={disabled}
          onChange={(_event, data) => onChange(name.anzeige, data.checked === true)}
        />
      ))}
      <Caption1 className={styles.gedaempft}>{LEITUNG_DATENSCHUTZ}</Caption1>
    </fieldset>
  );
}

/**
 * Der Abgleich an einer vorhandenen Organisation (V8): wer schon leitet, steht
 * als Satz da; wer neu in der Signatur ist, bekommt ein Haekchen wie in V5; wer
 * nicht mehr darin steht, einen Satz und einmal den Weg zu den Beziehungen.
 * Beendet wird hier nichts.
 */
export function LeitungAbgleichGruppe({
  organisation,
  organisationId,
  zustand,
  gewaehlt,
  onChange,
  disabled,
}: {
  readonly organisation: string;
  readonly organisationId: string;
  readonly zustand: 'laedt' | 'gescheitert' | LeitungAbgleich;
  readonly gewaehlt: ReadonlySet<string>;
  readonly onChange: (anzeige: string, an: boolean) => void;
  readonly disabled: boolean;
}): ReactElement {
  const styles = useStyles();
  return (
    <fieldset id="qc-leitung" tabIndex={-1} className={styles.gruppe}>
      <legend className={styles.legende}>{LEITUNG_ABGLEICH_TITEL}</legend>
      {/* Die Region steht schon beim Laden da, damit das Ergebnis angesagt wird; der Fokus bleibt (B16).
          Scheitert der Vergleich, genuegt der sichtbare Satz. Nur aria-live, keine zweite role=status:
          die gehoert allein dem Ergebnis der Anlage (Codex-P2 #816), wie bei Erkennung und Suche. */}
      <span aria-live="polite" className={styles.verborgen} data-leitung-ansage="">
        {typeof zustand === 'object' ? leitungAbgleichAnsage(organisation, zustand) : ''}
      </span>
      {zustand === 'laedt' ? (
        <Caption1 className={styles.gedaempft}>Wird mit der Leitung von {organisation} verglichen …</Caption1>
      ) : zustand === 'gescheitert' ? (
        <Caption1>{LEITUNG_ABGLEICH_GESCHEITERT}</Caption1>
      ) : (
        <>
          <Caption1>{LEITUNG_ABGLEICH_GRENZE}</Caption1>
          {zustand.schonDa.map((name) => (
            <Body1 key={`da-${name}`}>{leitungSchonDa(name, organisation)}</Body1>
          ))}
          {zustand.neu.length === 0 ? undefined : (
            <>
              <Caption1>{leitungSatz(organisation)}</Caption1>
              {zustand.neu.map((name) => (
                <Checkbox
                  key={name.anzeige}
                  label={name.anzeige}
                  checked={gewaehlt.has(name.anzeige)}
                  disabled={disabled}
                  onChange={(_event, data) => onChange(name.anzeige, data.checked === true)}
                />
              ))}
            </>
          )}
          {zustand.nichtMehr.map((name) => (
            <Body1 key={`weg-${name}`}>{leitungNichtMehr(name)}</Body1>
          ))}
          {zustand.nichtMehr.length === 0 ? undefined : (
            // In einem neuen Tab: das Anlegen haelt keinen Entwurf, ein Wechsel hier verwuerfe die Eingaben.
            <a href={partyHref(organisationId)} target="_blank" rel="noopener noreferrer" aria-label={`${beziehungenAnsehen(organisation)}, öffnet in neuem Tab`}>
              {textVorNeuerTab(beziehungenAnsehen(organisation)).vorne}
              {/* Das letzte Wort bleibt beim Zeichen (B17). */}
              <span style={{ whiteSpace: 'nowrap' }}>
                {textVorNeuerTab(beziehungenAnsehen(organisation)).hinten}
                <NeuerTabZeichen />
              </span>
            </a>
          )}
          {zustand.neu.length === 0 ? undefined : <Caption1 className={styles.gedaempft}>{LEITUNG_DATENSCHUTZ}</Caption1>}
        </>
      )}
    </fieldset>
  );
}
